import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TierBadge } from './TierBadge';
import { timeAgo, formatLatency } from '@/lib/utils';
import { 
  Database, 
  Shield, 
  Clock,
  AlertCircle
} from 'lucide-react';

export function ActivityFeed({ activities, loading }) {
  if (loading) {
    return (
      <Card className="border-border bg-card h-full">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse flex items-center gap-3">
                <div className="h-8 w-8 rounded bg-secondary" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-3/4 bg-secondary rounded" />
                  <div className="h-2 w-1/2 bg-secondary rounded" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <Card className="border-border bg-card h-full">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center h-48 text-muted-foreground">
          <AlertCircle className="h-8 w-8 mb-2" />
          <p className="text-sm">No recent activity</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border bg-card h-full" data-testid="activity-feed">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[320px] px-4 pb-4">
          <div className="space-y-1">
            {activities.map((activity) => (
              <div
                key={activity.id}
                className="activity-item rounded-md hover:bg-secondary/30 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <TierBadge tier={activity.tier} size="sm" showLabel={false} />
                      <span className="text-xs font-mono text-muted-foreground capitalize">
                        {activity.intent}
                      </span>
                    </div>
                    <p className="text-sm text-foreground truncate">
                      {activity.query_preview}
                    </p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatLatency(activity.latency_ms)}
                      </span>
                      {activity.cache_hit && (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-400">
                          <Database className="h-3 w-3" />
                          Cache Hit
                        </span>
                      )}
                      {activity.pii_detected && (
                        <span className="flex items-center gap-1 text-[10px] text-amber-400">
                          <Shield className="h-3 w-3" />
                          PII
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {timeAgo(activity.timestamp)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

export default ActivityFeed;
